import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Button, TextField, IconButton,
  Tooltip, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, Tabs, Tab, Divider,
  Checkbox
} from '@mui/material';
import {
  AccountTree as TreeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CompareArrows as DiffIcon,
  CheckCircle as CheckIcon,
  Warning as WarnIcon,
  School as SchoolIcon,
  Visibility as VisibleIcon,
  VisibilityOff as HideIcon,
  Download as SeedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

const SEM_COLORS = ['', '#3498db', '#e67e22', '#2ecc71', '#9b59b6', '#e74c3c', '#16a085'];
const YEARS = [1, 2, 3];
const yearSems = (y) => [y * 2 - 1, y * 2];

// ── Small helpers ─────────────────────────────────────────────────────────────
const SemTab = ({ s, count, active }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="caption" fontWeight={700} sx={{ color: active ? SEM_COLORS[s] : 'inherit' }}>
      S{s}
    </Typography>
    {count !== undefined && (
      <Typography variant="caption" display="block" sx={{ color: 'text.disabled', fontSize: 10 }}>
        {count}
      </Typography>
    )}
  </Box>
);

// ── Module row ────────────────────────────────────────────────────────────────
const ModuleRow = ({ mod, selected, onSelect, onEdit, onDelete, diffMode }) => {
  const inDb = mod.in_db;
  const accent = SEM_COLORS[mod.semester_number] || '#607d8b';

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 2, py: 1, borderRadius: 2,
      bgcolor: !mod.is_active ? '#fafafa' : inDb ? 'white' : '#fffbf0',
      border: '1px solid',
      borderColor: !mod.is_active ? '#f0f0f0' : diffMode && !inDb ? '#f39c12' : '#f0f0f0',
      opacity: mod.is_active ? 1 : 0.5,
      '&:hover': { borderColor: accent + '55', bgcolor: accent + '08' },
    }}>
      <Checkbox
        size="small" checked={selected}
        onChange={(e) => onSelect(mod.id, e.target.checked)}
        sx={{ p: 0.5 }}
      />

      {/* Code badge */}
      <Typography variant="caption" sx={{
        fontFamily: 'monospace', fontWeight: 700, px: 1, py: 0.3,
        borderRadius: 1, bgcolor: accent + '18', color: accent,
        flexShrink: 0, minWidth: 96,
      }}>
        {mod.cod_elp}
      </Typography>

      {/* Label */}
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {mod.label || mod.db_label || '—'}
      </Typography>

      {/* Credits */}
      {mod.credits && (
        <Chip label={`${mod.credits} cr`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#f1f5f9' }} />
      )}

      {/* DB status badge — only in diff mode */}
      {diffMode && (
        inDb
          ? <Chip icon={<CheckIcon sx={{ fontSize: '12px !important' }} />} label="DB" size="small"
              sx={{ height: 20, bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700, fontSize: 10 }} />
          : <Chip icon={<WarnIcon sx={{ fontSize: '12px !important' }} />} label="Manquant" size="small"
              sx={{ height: 20, bgcolor: '#fef3c7', color: '#b45309', fontWeight: 700, fontSize: 10 }} />
      )}

      {/* Actions */}
      <Box sx={{ flexShrink: 0, display: 'flex', gap: 0.5 }}>
        <Tooltip title="Modifier">
          <IconButton size="small" onClick={() => onEdit(mod)} sx={{ color: '#3498db' }}>
            <EditIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Supprimer">
          <IconButton size="small" onClick={() => onDelete(mod.id)} sx={{ color: '#e74c3c' }}>
            <DeleteIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ── Deprecated DB rows (diff mode) ───────────────────────────────────────────
const DeprecatedRow = ({ mod }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
    borderRadius: 2, bgcolor: '#fff5f5', border: '1px solid #fca5a5',
  }}>
    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, px: 1, py: 0.3, borderRadius: 1, bgcolor: '#fee2e2', color: '#dc2626', flexShrink: 0, minWidth: 96 }}>
      {mod.cod_elp}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1 }} color="text.secondary" noWrap>
      {mod.lib_elp || mod.lib_elp_arb || '—'}
    </Typography>
    <Chip label="Oracle seulement" size="small" sx={{ height: 20, bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 10 }} />
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const PedagogyStructure = () => {
  const [filieres, setFilieres]           = useState([]);
  const [selectedCod, setSelectedCod]     = useState(null);
  const [semModules, setSemModules]        = useState({});   // { 1:[], 2:[], ... }
  const [activeSem, setActiveSem]          = useState(1);
  const [loading, setLoading]             = useState(false);
  const [loadingFil, setLoadingFil]       = useState(true);
  const [diffData, setDiffData]           = useState(null);
  const [diffMode, setDiffMode]           = useState(false);
  const [loadingDiff, setLoadingDiff]     = useState(false);
  const [search, setSearch]               = useState('');
  const [selected, setSelected]           = useState(new Set());
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  // Dialogs
  const [addFilDialog, setAddFilDialog]   = useState(false);
  const [addModDialog, setAddModDialog]   = useState(false);
  const [editModDialog, setEditModDialog] = useState(false);
  const [editFilDialog, setEditFilDialog] = useState(false);
  const [seedDialog, setSeedDialog]       = useState(false);
  const [editMod, setEditMod]             = useState(null);
  const [saving, setSaving]               = useState(false);

  // Form state
  const [filForm, setFilForm]   = useState({ cod: '', label_ar: '', label_fr: '', lang: 'ar' });
  const [modForm, setModForm]   = useState({ cod_elp: '', label: '', credits: '', semester_number: 1 });

  const notify = (msg, type = 'success') => {
    if (type === 'error') setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  // Load filières list
  const loadFilieres = useCallback(async () => {
    setLoadingFil(true);
    try {
      const data = await adminAPI.getHierarchyFilieres();
      setFilieres(Array.isArray(data) ? data : []);
    } catch (e) {
      notify('Erreur chargement filières: ' + e.message, 'error');
    } finally {
      setLoadingFil(false);
    }
  }, []);

  useEffect(() => { loadFilieres(); }, [loadFilieres]);

  // Load modules for selected filière
  const loadModules = useCallback(async (cod) => {
    if (!cod) return;
    setLoading(true);
    setSelected(new Set());
    try {
      const data = await adminAPI.getHierarchyModules(cod);
      setSemModules(data || {});
      setActiveSem(1);
    } catch (e) {
      notify('Erreur chargement modules: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const selectFiliere = (cod) => {
    setSelectedCod(cod);
    setDiffData(null);
    setDiffMode(false);
    loadModules(cod);
  };

  // Load diff
  const loadDiff = async () => {
    setLoadingDiff(true);
    try {
      const data = await adminAPI.getHierarchyDiff(selectedCod);
      setDiffData(data);
      setDiffMode(true);
    } catch (e) {
      notify('Erreur diff: ' + e.message, 'error');
    } finally {
      setLoadingDiff(false);
    }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleAddFiliere = async () => {
    if (!filForm.cod) return;
    setSaving(true);
    try {
      await adminAPI.createFiliere(filForm);
      setAddFilDialog(false);
      setFilForm({ cod: '', label_ar: '', label_fr: '', lang: 'ar' });
      await loadFilieres();
      notify('Filière créée');
    } catch (e) { notify(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleUpdateFiliere = async () => {
    setSaving(true);
    try {
      await adminAPI.updateFiliere(selectedCod, filForm);
      setEditFilDialog(false);
      await loadFilieres();
      notify('Filière mise à jour');
    } catch (e) { notify(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteFiliere = async (cod) => {
    if (!window.confirm(`Supprimer la filière ${cod} et tous ses modules ?`)) return;
    try {
      await adminAPI.deleteFiliere(cod);
      if (selectedCod === cod) { setSelectedCod(null); setSemModules({}); }
      await loadFilieres();
      notify('Filière supprimée');
    } catch (e) { notify(e.message, 'error'); }
  };

  const handleAddModule = async () => {
    if (!modForm.cod_elp || !selectedCod) return;
    setSaving(true);
    try {
      await adminAPI.createHierarchyModule({ ...modForm, filiere_cod: selectedCod });
      setAddModDialog(false);
      setModForm({ cod_elp: '', label: '', credits: '', semester_number: activeSem });
      await loadModules(selectedCod);
      notify('Module ajouté');
    } catch (e) { notify(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleEditModule = async () => {
    setSaving(true);
    try {
      await adminAPI.updateHierarchyModule(editMod.id, {
        label: modForm.label,
        credits: modForm.credits ? parseInt(modForm.credits) : null,
        semester_number: modForm.semester_number,
        is_active: modForm.is_active,
      });
      setEditModDialog(false);
      await loadModules(selectedCod);
      notify('Module mis à jour');
    } catch (e) { notify(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteModule = async (id) => {
    if (!window.confirm('Supprimer ce module de la hiérarchie ?')) return;
    try {
      await adminAPI.deleteHierarchyModule(id);
      await loadModules(selectedCod);
      notify('Module supprimé');
    } catch (e) { notify(e.message, 'error'); }
  };

  const handleBulk = async (is_active) => {
    if (selected.size === 0) return;
    try {
      await adminAPI.bulkHierarchyModules(Array.from(selected), is_active);
      setSelected(new Set());
      await loadModules(selectedCod);
      notify(`${selected.size} module(s) ${is_active ? 'activé(s)' : 'désactivé(s)'}`);
    } catch (e) { notify(e.message, 'error'); }
  };

  const handleSeed = async () => {
    setSaving(true);
    try {
      const r = await adminAPI.seedHierarchy();
      setSeedDialog(false);
      await loadFilieres();
      if (selectedCod) await loadModules(selectedCod);
      notify(`Seed terminé: ${r.filieres} filières, ${r.modules} modules`);
    } catch (e) { notify(e.message, 'error'); }
    finally { setSaving(false); }
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const currentFiliere = filieres.find(f => f.cod === selectedCod);
  const currentMods    = semModules[activeSem] || [];
  const filteredMods   = currentMods.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.cod_elp.toLowerCase().includes(q) || (m.label || '').toLowerCase().includes(q);
  });

  const semCounts = {};
  for (let s = 1; s <= 6; s++) {
    semCounts[s] = (semModules[s] || []).filter(m => m.is_active).length;
  }

  // Deprecated modules for current semester in diff mode
  const deprecatedForSem = diffData?.deprecated_in_db?.filter(m => {
    if (!m.cod_elp) return false;
    const semChar = parseInt(m.cod_elp[4]);
    return semChar === activeSem && m.cod_elp.startsWith(selectedCod);
  }) || [];

  const toggleSelect = (id, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };
  const toggleSelectAll = (checked) => {
    if (checked) setSelected(new Set(filteredMods.map(m => m.id)));
    else setSelected(new Set());
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #16a085 100%)',
        borderRadius: 4, p: { xs: 2, md: 3 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TreeIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>Structure Pédagogique</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Filières → Semestres → Modules · Source de vérité pour la Licence
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" startIcon={<SeedIcon />}
            onClick={() => setSeedDialog(true)}
            sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', borderRadius: 2, fontSize: '0.78rem' }}>
            Importer Excel
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => { setFilForm({ cod: '', label_ar: '', label_fr: '', lang: 'ar' }); setAddFilDialog(true); }}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, fontSize: '0.78rem', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
            + Filière
          </Button>
        </Box>
      </Box>

      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>

        {/* ── Left: filière list ── */}
        <Paper elevation={0} sx={{ width: 230, flexShrink: 0, borderRadius: 3, border: '1.5px solid #e8ecef', overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8f9fa', borderBottom: '1px solid #e8ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">FILIÈRES</Typography>
            <Tooltip title="Actualiser">
              <IconButton size="small" onClick={loadFilieres}><RefreshIcon sx={{ fontSize: 15 }} /></IconButton>
            </Tooltip>
          </Box>

          {loadingFil ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
          ) : (
            <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
              {filieres.map(f => (
                <Box key={f.cod}
                  onClick={() => selectFiliere(f.cod)}
                  sx={{
                    px: 2, py: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                    borderLeft: '3px solid',
                    borderColor: selectedCod === f.cod ? '#16a085' : 'transparent',
                    bgcolor: selectedCod === f.cod ? '#f0fdf9' : 'transparent',
                    '&:hover': { bgcolor: '#f8f9fa' },
                  }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color: '#16a085', fontFamily: 'monospace' }}>
                      {f.cod}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ fontSize: 11 }}>
                      {f.label_ar || f.label_fr || '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip label={f.module_count || 0} size="small"
                      sx={{ height: 18, fontSize: 10, bgcolor: selectedCod === f.cod ? '#d1fae5' : '#f1f5f9', color: selectedCod === f.cod ? '#065f46' : '#64748b' }} />
                    <Tooltip title="Supprimer filière">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteFiliere(f.cod); }}
                        sx={{ color: '#cbd5e1', '&:hover': { color: '#e74c3c' }, p: 0.3 }}>
                        <DeleteIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
              {filieres.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.disabled">Aucune filière</Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* ── Right: module panel ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!selectedCod ? (
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px dashed #e0e0e0', p: 6, textAlign: 'center' }}>
              <SchoolIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
              <Typography variant="h6" color="text.secondary" fontWeight={600}>Sélectionnez une filière</Typography>
              <Typography variant="body2" color="text.disabled">pour voir et gérer les modules par semestre</Typography>
            </Paper>
          ) : (
            <>
              {/* Filière header */}
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', mb: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e8ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ fontFamily: 'monospace', color: '#16a085' }}>{selectedCod}</Typography>
                      <Chip label={currentFiliere?.lang === 'fr' ? 'Français' : 'Arabe'} size="small"
                        sx={{ height: 20, bgcolor: currentFiliere?.lang === 'fr' ? '#dbeafe' : '#fef3c7', color: currentFiliere?.lang === 'fr' ? '#1d4ed8' : '#92400e', fontSize: 10 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {currentFiliere?.label_ar || currentFiliere?.label_fr || ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Modifier la filière">
                      <IconButton size="small" onClick={() => {
                        setFilForm({ label_ar: currentFiliere?.label_ar || '', label_fr: currentFiliere?.label_fr || '', lang: currentFiliere?.lang || 'ar', is_active: currentFiliere?.is_active });
                        setEditFilDialog(true);
                      }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={diffMode ? 'Masquer diff' : 'Vue diff Oracle'}>
                      <IconButton size="small" onClick={diffMode ? () => setDiffMode(false) : loadDiff}
                        sx={{ color: diffMode ? '#e74c3c' : '#64748b' }}>
                        {loadingDiff ? <CircularProgress size={16} /> : diffMode ? <CloseIcon sx={{ fontSize: 16 }} /> : <DiffIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Actualiser">
                      <IconButton size="small" onClick={() => loadModules(selectedCod)}><RefreshIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Year / Semester tabs */}
                {[1, 2, 3].map(yr => (
                  <Box key={yr} sx={{ borderBottom: yr < 3 ? '1px solid #f0f0f0' : 'none' }}>
                    <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.disabled" sx={{ minWidth: 52 }}>
                        Année {yr}
                      </Typography>
                      {yearSems(yr).map(s => (
                        <Box key={s}
                          onClick={() => setActiveSem(s)}
                          sx={{
                            px: 1.8, py: 0.8, borderRadius: 2, cursor: 'pointer',
                            bgcolor: activeSem === s ? SEM_COLORS[s] + '18' : 'transparent',
                            border: '1.5px solid',
                            borderColor: activeSem === s ? SEM_COLORS[s] : 'transparent',
                            '&:hover': { bgcolor: SEM_COLORS[s] + '12' },
                          }}>
                          <SemTab s={s} count={semCounts[s]} active={activeSem === s} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Paper>

              {/* Toolbar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  size="small" placeholder="Rechercher code ou libellé…"
                  value={search} onChange={e => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 17, mr: 0.5, color: '#94a3b8' }} /> }}
                  sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                {selected.size > 0 && (
                  <>
                    <Button size="small" startIcon={<VisibleIcon />} onClick={() => handleBulk(true)}
                      sx={{ borderRadius: 2, bgcolor: '#dcfce7', color: '#16a34a', '&:hover': { bgcolor: '#bbf7d0' } }}>
                      Activer ({selected.size})
                    </Button>
                    <Button size="small" startIcon={<HideIcon />} onClick={() => handleBulk(false)}
                      sx={{ borderRadius: 2, bgcolor: '#fee2e2', color: '#dc2626', '&:hover': { bgcolor: '#fecaca' } }}>
                      Désactiver ({selected.size})
                    </Button>
                  </>
                )}
                <Button variant="contained" size="small" startIcon={<AddIcon />}
                  onClick={() => { setModForm({ cod_elp: '', label: '', credits: '', semester_number: activeSem }); setAddModDialog(true); }}
                  sx={{ borderRadius: 2, bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' }, flexShrink: 0 }}>
                  + Module S{activeSem}
                </Button>
              </Box>

              {/* Module list */}
              {loading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
              ) : (
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e8ecef', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox size="small" sx={{ p: 0.5 }}
                      checked={filteredMods.length > 0 && selected.size === filteredMods.length}
                      indeterminate={selected.size > 0 && selected.size < filteredMods.length}
                      onChange={e => toggleSelectAll(e.target.checked)} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      S{activeSem} — {filteredMods.length} module(s)
                    </Typography>
                    {diffMode && (
                      <Chip icon={<DiffIcon sx={{ fontSize: '12px !important' }} />} label="Mode diff actif" size="small"
                        sx={{ height: 20, bgcolor: '#fef3c7', color: '#b45309', fontSize: 10, ml: 'auto' }} />
                    )}
                  </Box>

                  <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 460, overflowY: 'auto' }}>
                    {filteredMods.length === 0 && !diffMode && (
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.disabled">
                          {search ? 'Aucun résultat' : `Aucun module en S${activeSem}`}
                        </Typography>
                      </Box>
                    )}
                    {filteredMods.map(mod => (
                      <ModuleRow key={mod.id} mod={mod}
                        selected={selected.has(mod.id)}
                        onSelect={toggleSelect}
                        diffMode={diffMode}
                        onEdit={(m) => {
                          setEditMod(m);
                          setModForm({ label: m.label || '', credits: m.credits || '', semester_number: m.semester_number, is_active: m.is_active });
                          setEditModDialog(true);
                        }}
                        onDelete={handleDeleteModule}
                      />
                    ))}

                    {/* Deprecated Oracle-only codes in diff mode */}
                    {diffMode && deprecatedForSem.length > 0 && (
                      <>
                        <Divider sx={{ my: 1 }}>
                          <Chip label={`${deprecatedForSem.length} codes Oracle non dans la hiérarchie`} size="small"
                            sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontSize: 10 }} />
                        </Divider>
                        {deprecatedForSem.map(m => <DeprecatedRow key={m.cod_elp} mod={m} />)}
                      </>
                    )}
                  </Box>
                </Paper>
              )}

              {/* Diff summary */}
              {diffMode && diffData && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 2, border: '1.5px solid #fef3c7', bgcolor: '#fffbeb' }}>
                    <Typography variant="caption" fontWeight={700} color="#b45309">
                      Codes dans hiérarchie mais absents de la DB : {diffData.missing_in_db?.length || 0}
                    </Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 2, border: '1.5px solid #fecaca', bgcolor: '#fff5f5' }}>
                    <Typography variant="caption" fontWeight={700} color="#dc2626">
                      Codes Oracle non dans la hiérarchie (dépréciés) : {diffData.deprecated_in_db?.length || 0}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* ── Add Filière dialog ── */}
      <Dialog open={addFilDialog} onClose={() => setAddFilDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvelle Filière</DialogTitle>
        <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Code (ex: JLAP)" value={filForm.cod} onChange={e => setFilForm(p => ({ ...p, cod: e.target.value.toUpperCase() }))} size="small" required />
          <TextField label="Libellé arabe" value={filForm.label_ar} onChange={e => setFilForm(p => ({ ...p, label_ar: e.target.value }))} size="small" inputProps={{ dir: 'rtl' }} />
          <TextField label="Libellé français" value={filForm.label_fr} onChange={e => setFilForm(p => ({ ...p, label_fr: e.target.value }))} size="small" />
          <FormControl size="small">
            <InputLabel>Langue principale</InputLabel>
            <Select value={filForm.lang} onChange={e => setFilForm(p => ({ ...p, lang: e.target.value }))} label="Langue principale">
              <MenuItem value="ar">Arabe</MenuItem>
              <MenuItem value="fr">Français</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFilDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleAddFiliere} disabled={saving || !filForm.cod}
            sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Filière dialog ── */}
      <Dialog open={editFilDialog} onClose={() => setEditFilDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier {selectedCod}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Libellé arabe" value={filForm.label_ar || ''} onChange={e => setFilForm(p => ({ ...p, label_ar: e.target.value }))} size="small" inputProps={{ dir: 'rtl' }} />
          <TextField label="Libellé français" value={filForm.label_fr || ''} onChange={e => setFilForm(p => ({ ...p, label_fr: e.target.value }))} size="small" />
          <FormControlLabel control={<Switch checked={!!filForm.is_active} onChange={e => setFilForm(p => ({ ...p, is_active: e.target.checked }))} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFilDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateFiliere} disabled={saving}
            sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Module dialog ── */}
      <Dialog open={addModDialog} onClose={() => setAddModDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un module — {selectedCod}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Code module (ex: JLAP1105)" value={modForm.cod_elp} onChange={e => setModForm(p => ({ ...p, cod_elp: e.target.value.toUpperCase() }))} size="small" required />
          <TextField label="Libellé" value={modForm.label} onChange={e => setModForm(p => ({ ...p, label: e.target.value }))} size="small" />
          <TextField label="Crédits" type="number" value={modForm.credits} onChange={e => setModForm(p => ({ ...p, credits: e.target.value }))} size="small" />
          <FormControl size="small">
            <InputLabel>Semestre</InputLabel>
            <Select value={modForm.semester_number} onChange={e => setModForm(p => ({ ...p, semester_number: e.target.value }))} label="Semestre">
              {[1,2,3,4,5,6].map(s => <MenuItem key={s} value={s}>S{s}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleAddModule} disabled={saving || !modForm.cod_elp}
            sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Module dialog ── */}
      <Dialog open={editModDialog} onClose={() => setEditModDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier module <Typography component="span" sx={{ fontFamily: 'monospace', color: '#16a085' }}>{editMod?.cod_elp}</Typography></DialogTitle>
        <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Libellé" value={modForm.label || ''} onChange={e => setModForm(p => ({ ...p, label: e.target.value }))} size="small" />
          <TextField label="Crédits" type="number" value={modForm.credits || ''} onChange={e => setModForm(p => ({ ...p, credits: e.target.value }))} size="small" />
          <FormControl size="small">
            <InputLabel>Semestre</InputLabel>
            <Select value={modForm.semester_number || 1} onChange={e => setModForm(p => ({ ...p, semester_number: e.target.value }))} label="Semestre">
              {[1,2,3,4,5,6].map(s => <MenuItem key={s} value={s}>S{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel control={<Switch checked={!!modForm.is_active} onChange={e => setModForm(p => ({ ...p, is_active: e.target.checked }))} />} label="Actif" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleEditModule} disabled={saving}
            sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Seed confirmation dialog ── */}
      <Dialog open={seedDialog} onClose={() => setSeedDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Importer depuis les fichiers Excel</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Ceci va importer/réimporter toutes les filières et modules définis dans les fichiers <code>modules_hierarchy/</code> (opération sûre — utilise ON CONFLICT DO UPDATE).
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeedDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSeed} disabled={saving}
            sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Importer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PedagogyStructure;
